import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Modal, ModalBody, ModalFooter, Button } from 'reactstrap';
import Moment from 'react-moment';
import { ClipLoader } from 'react-spinners';

import { fetchMappingAudit } from '../../../../../actions/synchronisationActions';

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 300;

const ACTION_CLASS = {
  CREATE: 'mah-chip--create',
  UPDATE: 'mah-chip--update',
  DELETE: 'mah-chip--delete'
};

class MappingAuditModal extends Component {
  state = { search: '' };

  componentDidUpdate(prevProps) {
    // (re)load the first page whenever the modal is opened
    if (!prevProps.isOpen && this.props.isOpen && this.props.storeId) {
      this.setState({ search: '' });
      this.load(0, '');
    }
  }

  componentWillUnmount() {
    clearTimeout(this.debounce);
  }

  load(page, search) {
    const { storeId, sourceId, fetchMappingAudit } = this.props;
    fetchMappingAudit(storeId, sourceId, page, PAGE_SIZE, search);
  }

  paginate = page => {
    const total = (this.props.audit.page && this.props.audit.page.totalPages) || 0;
    const target = Math.max(0, Math.min(page, total - 1)); // never navigate out of range
    this.load(target, this.state.search);
  };

  handleSearch = e => {
    const search = e.target.value;
    this.setState({ search });
    clearTimeout(this.debounce);
    this.debounce = setTimeout(() => this.load(0, search), SEARCH_DEBOUNCE_MS);
  };

  clearSearch = () => {
    this.setState({ search: '' });
    clearTimeout(this.debounce);
    this.load(0, '');
  };

  // CREATE/DELETE store a JSON snapshot of the whole mapping; parse it (null on failure).
  parseSnapshot(value) {
    try {
      return JSON.parse(value);
    } catch (e) {
      return null;
    }
  }

  renderDefinition(snapshot) {
    const source = [snapshot.sourceTable, snapshot.sourceColumn].filter(Boolean).join('.');
    const target = [snapshot.targetTable, snapshot.targetColumn].filter(Boolean).join('.');
    const tags = [
      snapshot.converter && ['converter', snapshot.converter],
      snapshot.defaultValue && ['default', snapshot.defaultValue],
      snapshot.rowGroup && ['group', snapshot.rowGroup]
    ].filter(Boolean);

    return (
      <div className="mah-def">
        <span className="mah-def-flow">
          <span className="mah-mono">{source || '∅'}</span>
          <i className="fa-solid fa-arrow-right-long mah-arrow" />
          <span className="mah-mono">{target || '∅'}</span>
        </span>
        {tags.length > 0 && (
          <span className="mah-tags">
            {tags.map(([key, value]) => (
              <span className="mah-tag" key={key}>
                <span className="mah-tag-k">{key}</span>
                <span className="mah-mono">{value}</span>
              </span>
            ))}
          </span>
        )}
      </div>
    );
  }

  renderChange(row) {
    if (row.action === 'UPDATE') {
      const hasOld = row.oldValue != null && row.oldValue !== '';
      const hasNew = row.newValue != null && row.newValue !== '';
      return (
        <span className="mah-diff">
          {row.changedColumn && <span className="mah-diff-col mah-mono">{row.changedColumn}</span>}
          {hasOld ? <span className="mah-diff-old mah-mono">{row.oldValue}</span> : <span className="mah-diff-empty">&empty;</span>}
          <i className="fa-solid fa-arrow-right-long mah-arrow" />
          {hasNew ? <span className="mah-diff-new mah-mono">{row.newValue}</span> : <span className="mah-diff-empty">&empty;</span>}
        </span>
      );
    }

    // CREATE / DELETE — full mapping snapshot
    const raw = row.action === 'DELETE' ? row.oldValue : row.newValue;
    const snapshot = raw != null ? this.parseSnapshot(raw) : null;
    if (snapshot) {
      return (
        <div className={row.action === 'DELETE' ? 'mah-snapshot mah-snapshot--removed' : 'mah-snapshot mah-snapshot--added'}>
          {this.renderDefinition(snapshot)}
        </div>
      );
    }
    return <code className="mah-raw">{raw}</code>;
  }

  render() {
    const { isOpen, toggle, audit } = this.props;
    const { loading, page } = audit;
    const { search } = this.state;
    const rows = (page && page.content) || [];
    const total = (page && page.totalElements) || 0;
    const number = (page && page.number) || 0;
    const size = (page && page.size) || PAGE_SIZE;
    const totalPages = (page && page.totalPages) || 0;
    const start = total === 0 ? 0 : number * size + 1;
    const end = Math.min(number * size + rows.length, total);
    const searching = search.trim() !== '';

    return (
      <Modal isOpen={isOpen} toggle={toggle} scrollable className="mah-dialog" contentClassName="mah-modal">
        <div className="mah-header">
          <div className="mah-header-titles">
            <span className="mah-title">
              <i className="fa-solid fa-history" /> Mapping change history
            </span>
            <span className="mah-subtitle">{total > 0 ? `${total} change${total === 1 ? '' : 's'} recorded` : 'Audit trail'}</span>
          </div>
          <button type="button" className="mah-close" aria-label="Close" onClick={toggle}>
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        <div className="mah-toolbar">
          <div className="mah-search">
            <i className="fa-solid fa-magnifying-glass" />
            <input
              type="text"
              value={search}
              onChange={this.handleSearch}
              placeholder="Search by user, mapping, column or value…"
            />
            {search && (
              <button type="button" className="mah-search-clear" aria-label="Clear search" onClick={this.clearSearch}>
                <i className="fa-solid fa-xmark" />
              </button>
            )}
          </div>
        </div>

        <ModalBody className="mah-body">
          {loading && (
            <div className="mah-state">
              <ClipLoader size={26} color={'#29363d'} loading={true} />
            </div>
          )}
          {!loading && rows.length === 0 && (
            <div className="mah-state mah-empty">
              <i className="fa-solid fa-inbox" />
              <p>{searching ? 'No changes match your search.' : 'No change history for these mappings.'}</p>
            </div>
          )}
          {!loading && rows.length > 0 && (
            <table className="mah-table">
              <thead>
                <tr>
                  <th style={{ width: '140px' }}>When</th>
                  <th style={{ width: '220px' }}>Who</th>
                  <th style={{ width: '104px' }}>Action</th>
                  <th style={{ width: '260px' }}>Mapping</th>
                  <th>Old &rarr; New</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(row => (
                  <tr key={row.id} className={`mah-row mah-row--${(row.action || '').toLowerCase()}`}>
                    <td className="mah-when" data-label="When">
                      {row.changedAt && (
                        <>
                          <Moment className="mah-when-abs" format="Do MMM YYYY, HH:mm" withTitle>
                            {new Date(row.changedAt)}
                          </Moment>
                          <Moment className="mah-when-rel" fromNow>
                            {new Date(row.changedAt)}
                          </Moment>
                        </>
                      )}
                    </td>
                    <td data-label="Who">
                      {row.changedBy ? (
                        <span className="mah-who-name">{row.changedBy}</span>
                      ) : (
                        <span className="mah-who-name mah-who--system">System</span>
                      )}
                    </td>
                    <td data-label="Action">
                      <span className={`mah-chip ${ACTION_CLASS[row.action] || ''}`}>
                        <span className="mah-dot" />
                        {row.action}
                      </span>
                    </td>
                    <td data-label="Mapping">
                      <span className="mah-mono mah-mapping">{row.mapping || '—'}</span>
                    </td>
                    <td className="mah-change" data-label="Old → New">{this.renderChange(row)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </ModalBody>

        <ModalFooter className="mah-footer">
          <span className="mah-count">
            {total > 0 && (
              <>
                Showing <b>{start}</b>&ndash;<b>{end}</b> of <b>{total}</b>
              </>
            )}
          </span>
          <div className="mah-pager">
            {totalPages > 1 && (
              <span className="mah-pager-controls">
                <button type="button" className="mah-pager-btn" disabled={number <= 0 || loading} onClick={() => this.paginate(number - 1)}>
                  <i className="fa-solid fa-angle-left" /> Prev
                </button>
                <span className="mah-pager-pos">
                  Page {number + 1} of {totalPages}
                </span>
                <button type="button" className="mah-pager-btn" disabled={number >= totalPages - 1 || loading} onClick={() => this.paginate(number + 1)}>
                  Next <i className="fa-solid fa-angle-right" />
                </button>
              </span>
            )}
            <Button color="secondary" onClick={toggle}>
              Close
            </Button>
          </div>
        </ModalFooter>
      </Modal>
    );
  }
}

export default connect(
  state => ({
    audit: state.sync.mapping.audit
  }),
  dispatch => ({
    fetchMappingAudit: (storeId, sourceId, page, size, search) => dispatch(fetchMappingAudit(storeId, sourceId, page, size, search))
  })
)(MappingAuditModal);
